import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/features/auth/hooks/useSession';
import { api } from '@/lib/api';
import { HttpError } from '@/lib/http';
import { queryKeys } from '@/lib/query/keys';
import type { BulkDeleteConfirmCardData } from './types';

/**
 * "Are you sure?" card for a chat-proposed MASS delete (3+ objects).
 *
 * The agent NEVER deletes 3+ itself — it emits this card. The deletion happens
 * here, on a real user click, via the objects API, so the model can never bypass
 * the prompt. Self-contained (mirrors TaskResultCard's local-writeback pattern).
 * Each previewed item is clickable to open it first, via `onOpenItem`.
 */
function plural(objectType: string, n: number): string {
  return n === 1 ? objectType : `${objectType}s`;
}

type Phase = 'idle' | 'deleting' | 'done' | 'cancelled';

type Resolution = { status: 'deleted' | 'cancelled'; deleted: number; failed: number };

/** localStorage key for this card's resolved (deleted/cancelled) state. The card
 *  is persisted inside the chat message but its phase is local React state, so
 *  without this a hard refresh re-shows the actionable prompt. We key on the
 *  server-stamped `cardId` (unique per emit, persisted in the message) so the
 *  resolved state survives reload AND a later re-ask for the same objects — which
 *  produces a NEW cardId — gets a fresh prompt rather than a stale resolution.
 *  Falls back to the object set for cards with no cardId. Exported for tests. */
export function bulkDeleteResolutionKey(data: { cardId?: string; objectType: string; objectIds: string[] }): string {
  if (data.cardId) return `xavior:bulk-delete:${data.cardId}`;
  return `xavior:bulk-delete:${data.objectType}:${[...data.objectIds].sort().join(',')}`;
}

function readResolution(key: string): Resolution | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Resolution;
    return parsed?.status === 'deleted' || parsed?.status === 'cancelled' ? parsed : null;
  } catch {
    return null;
  }
}

function writeResolution(key: string, value: Resolution): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full/blocked — non-fatal, the card just won't survive reload */
  }
}

/**
 * #4318: a 2xx DELETE is NOT proof the object is gone — some delete paths return
 * success while the row stays active (failed soft-archive, partial provider
 * sync), and counting success purely on "the delete didn't throw" falsely
 * reports it deleted. Read the object back and mirror the x-agent invariant
 * (apps/x-agent object-handlers.ts #4553/#5235):
 *   - gone (404) OR `archived_at` set                        → 'deleted'
 *   - a real object body (has an id) still active (archived_at null) → 'active'
 *   - a flaky/abort/5xx read or an empty/inconclusive body   → 'inconclusive'
 * Only 'active' is a confirmed failure; 'inconclusive' stays optimistic so a
 * flaky GET never masks a real delete.
 */
async function verifyDeleted(
  token: string | null,
  objectType: string,
  id: string,
): Promise<'deleted' | 'active' | 'inconclusive'> {
  try {
    const { object } = await api.objects.getObject(token, objectType, id);
    const archivedAt = object?.archived_at ?? object?.archivedAt ?? null;
    // Only CONTRADICT the delete when the read-back returns a real object body
    // (has an id) that is explicitly still active. An empty/unparseable body is
    // inconclusive — keep the optimistic success rather than invent a failure.
    if (object?.id != null && archivedAt == null) return 'active';
    return 'deleted';
  } catch (err) {
    // A 404 is positive proof the object is gone. Any other throw (transport,
    // abort, 5xx) is inconclusive — keep the optimistic delete success.
    if (err instanceof HttpError && err.status === 404) return 'deleted';
    return 'inconclusive';
  }
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

function Chevron() {
  return (
    <svg className="rc-bd-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

export function BulkDeleteConfirmCard({
  data,
  onOpenItem,
}: {
  data: BulkDeleteConfirmCardData;
  /** Open one item so the user can inspect it before deciding. Omitted when the
   *  object type has no open route — items then render as plain text. The
   *  boardId lets the kanban open the specific board and highlight the task. */
  onOpenItem?: (item: { id: string; boardId?: string }) => void;
}) {
  const { token } = useSession();
  const qc = useQueryClient();

  const objectType = data.objectType ?? 'object';
  const objectIds = Array.isArray(data.objectIds) ? data.objectIds : [];
  const previewItems = Array.isArray(data.previewItems) ? data.previewItems : [];
  const count = typeof data.count === 'number' ? data.count : objectIds.length;
  const remaining = count - previewItems.length;
  const storageKey = bulkDeleteResolutionKey({ cardId: data.cardId, objectType, objectIds });

  // Restore the resolved (deleted/cancelled) state across reloads (incl. hard
  // refresh) so the card keeps that state instead of re-showing the prompt. Read
  // once on mount via the lazy initializers.
  const [phase, setPhase] = useState<Phase>(() => {
    const r = readResolution(storageKey);
    return r?.status === 'cancelled' ? 'cancelled' : r?.status === 'deleted' ? 'done' : 'idle';
  });
  const [deletedCount, setDeletedCount] = useState(() => readResolution(storageKey)?.deleted ?? 0);
  const [failedCount, setFailedCount] = useState(() => readResolution(storageKey)?.failed ?? 0);

  if (!objectIds.length) return null;

  async function handleConfirm() {
    setPhase('deleting');
    let ok = 0;
    let failed = 0;
    // Sequential — simple, and avoids hammering the API for big sets. Mirrors
    // KanbanModule.handleDeleteCard (the app's canonical object delete).
    for (const id of objectIds) {
      try {
        await api.objects.deleteObject(token ?? null, objectType, id);
      } catch {
        failed += 1;
        continue;
      }
      // #4318: a 2xx delete is not proof the object is gone — verify by reading
      // it back before counting it deleted, so the rendered copy stays honest.
      const verdict = await verifyDeleted(token ?? null, objectType, id);
      if (verdict === 'active') failed += 1;
      else ok += 1;
    }
    // Refresh any list/board reading these objects so they disappear immediately.
    qc.invalidateQueries({ queryKey: queryKeys.objects.byType(objectType) });
    // Persist so a reload (incl. hard refresh) shows "Deleted N", not the prompt.
    writeResolution(storageKey, { status: 'deleted', deleted: ok, failed });
    setDeletedCount(ok);
    setFailedCount(failed);
    setPhase('done');
  }

  if (phase === 'cancelled') {
    return (
      <div className="rc-bulkdelete">
        <div className="rc-bd-result is-muted">Cancelled — nothing was deleted.</div>
      </div>
    );
  }

  if (phase === 'done') {
    return (
      <div className="rc-bulkdelete">
        <div className="rc-bd-result">
          Deleted {deletedCount} {plural(objectType, deletedCount)}
          {failedCount > 0 ? ` — ${failedCount} could not be deleted` : ''}.
        </div>
      </div>
    );
  }

  const busy = phase === 'deleting';

  return (
    <div className="rc-bulkdelete" role="group" aria-label={`Confirm deleting ${count} ${plural(objectType, count)}`}>
      <div className="rc-bd-header">
        <span className="rc-bd-icon"><TrashIcon /></span>
        <span className="rc-bd-title">Delete {count} {plural(objectType, count)}?</span>
      </div>

      <ul className="rc-bd-list">
        {previewItems.map((item) =>
          onOpenItem ? (
            <li key={item.id}>
              <button
                type="button"
                className="rc-bd-item"
                onClick={() => onOpenItem({ id: item.id, boardId: item.boardId })}
                title={`Open ${item.title}`}
                aria-label={`Open ${item.title}`}
              >
                <span className="rc-bd-item-title">{item.title}</span>
                <Chevron />
              </button>
            </li>
          ) : (
            <li key={item.id} className="rc-bd-item">
              <span className="rc-bd-item-title">{item.title}</span>
            </li>
          ),
        )}
        {remaining > 0 && <li className="rc-bd-more">…and {remaining} more</li>}
      </ul>

      <div className="rc-bd-note">This can’t be undone.</div>

      <div className="rc-bd-actions">
        <button
          type="button"
          className="rc-bd-btn rc-bd-cancel"
          onClick={() => {
            // Persist so a reload keeps it cancelled (not the prompt). Keyed on
            // cardId, so a later re-ask for the same objects still gets a fresh card.
            writeResolution(storageKey, { status: 'cancelled', deleted: 0, failed: 0 });
            setPhase('cancelled');
          }}
          disabled={busy}
        >
          Cancel
        </button>
        <button type="button" className="rc-bd-btn rc-bd-delete" onClick={handleConfirm} disabled={busy}>
          {busy ? 'Deleting…' : `Delete ${count}`}
        </button>
      </div>
    </div>
  );
}
